import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../services/api';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface VideoResult {
  videoUrl: string;
  predictionId: string;
  model: string;
  elapsedMs: number;
}

interface ServiceStatus {
  configured: boolean;
  reachable: boolean;
  error?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const AIVideoGenerator: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<VideoResult | null>(null);
  const [history, setHistory] = useState<VideoResult[]>([]);
  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Check Replicate service status on mount
  useEffect(() => {
    let cancelled = false;
    const checkStatus = async () => {
      try {
        const res = await apiClient.get<ServiceStatus>('/ai-video/status');
        if (!cancelled) setStatus(res.data);
      } catch {
        if (!cancelled) setStatus({ configured: false, reachable: false, error: 'Unable to reach AI video service' });
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    };
    void checkStatus();
    return () => { cancelled = true; };
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError(null);
    setResult(null);

    try {
      const res = await apiClient.post<VideoResult>('/ai-video/generate', {
        prompt: prompt.trim(),
      });
      setResult(res.data);
      setHistory((prev) => [res.data, ...prev]);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Video generation failed')
          : 'Video generation failed';
      setError(msg);
    } finally {
      setGenerating(false);
    }
  }, [prompt]);

  /* ---------------------------------------------------------------- */
  /*  Sample prompts (billboard-specific)                              */
  /* ---------------------------------------------------------------- */
  const samplePrompts = [
    'A cinematic billboard ad for a luxury car dealership at sunset with golden light reflecting off vehicles',
    'An animated digital billboard ad showing fresh pizza being made with steam and melting cheese',
    'A vibrant fitness gym promotional video with energetic workout scenes and motivational text',
    'A real estate billboard ad showcasing a modern home with a drone flyover of the neighborhood',
    'A holiday season retail sale advertisement with falling snowflakes and gift boxes',
  ];

  return (
    <main className="bg-slate-50 dark:bg-slate-900">
      <div className="mx-auto max-w-4xl px-4 py-8 space-y-6">
        {/* Header */}
        <header className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 sm:text-3xl">
              AI Campaign Video Generator
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Describe the campaign video you want and our AI will generate it for you.
            </p>
          </div>
          <Link
            to="/campaign/start"
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:text-sm"
          >
            Start Campaign
          </Link>
        </header>

        {/* Service status banner */}
        {statusLoading ? (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-sm text-slate-500 dark:text-slate-400 animate-pulse">
            Checking AI video service status&hellip;
          </div>
        ) : status && !status.configured ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            <strong>Not configured:</strong> The AI video generation service is not set up on this
            server. Contact an administrator to configure the Replicate API token.
          </div>
        ) : status && !status.reachable ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            <strong>Unreachable:</strong> The AI video service is configured but could not be
            reached. It may be temporarily unavailable.
          </div>
        ) : (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            AI video generation is online and ready.
          </div>
        )}

        {/* Prompt input */}
        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm space-y-4">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Describe your video</h2>

          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="E.g. A cinematic 5-second billboard ad for a coffee brand with steaming cups and warm lighting..."
            rows={4}
            maxLength={2000}
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-3 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 resize-y"
          />

          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-slate-400">{prompt.length} / 2 000</span>
            <button
              onClick={handleGenerate}
              disabled={generating || !prompt.trim() || (status !== null && !status.configured)}
              className="inline-flex items-center rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? (
                <>
                  <svg
                    className="mr-2 h-4 w-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Generating&hellip;
                </>
              ) : (
                'Generate Video'
              )}
            </button>
          </div>

          {/* Sample prompts */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Try a sample prompt
            </p>
            <div className="flex flex-wrap gap-2">
              {samplePrompts.map((sp, i) => (
                <button
                  key={i}
                  onClick={() => setPrompt(sp)}
                  className="rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-1 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 hover:border-slate-300 focus:outline-none"
                >
                  {sp.length > 60 ? sp.slice(0, 60) + '…' : sp}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Error */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Result */}
        {result && (
          <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm space-y-4">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Generated Video</h2>
              <span className="text-xs text-slate-400">
                {(result.elapsedMs / 1000).toFixed(1)}s &middot; {result.model}
              </span>
            </div>

            <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-black">
              <video
                src={result.videoUrl}
                controls
                autoPlay
                loop
                className="w-full"
                style={{ maxHeight: 480 }}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <a
                href={result.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50"
              >
                Open in new tab
              </a>
              <a
                href={result.videoUrl}
                download
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700"
              >
                Download
              </a>
            </div>
          </section>
        )}

        {/* History */}
        {history.length > 1 && (
          <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm space-y-3">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Previous Generations</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {history.slice(1).map((h, i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-black"
                >
                  <video
                    src={h.videoUrl}
                    controls
                    className="w-full"
                    style={{ maxHeight: 240 }}
                  />
                  <div className="bg-white dark:bg-slate-900 px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                    {(h.elapsedMs / 1000).toFixed(1)}s &middot; {h.model}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default AIVideoGenerator;
